const statusLabels = {
  ACTIVE: "Ativo",
  INACTIVE: "Inativo",
  IN_PROCESS: "Em processo",
  PLANNED: "Planejado",
};

const dateBr = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : String(value || "");
};

const yearsFrom = (value) => {
  const birthday = value ? new Date(`${String(value).slice(0, 10)}T12:00:00`) : null;
  if (!birthday || Number.isNaN(birthday.getTime())) return "";
  const now = new Date();
  let years = now.getFullYear() - birthday.getFullYear();
  const beforeBirthday = now.getMonth() < birthday.getMonth()
    || (now.getMonth() === birthday.getMonth() && now.getDate() < birthday.getDate());
  if (beforeBirthday) years -= 1;
  return years >= 0 ? String(years) : "";
};

const currency = (cents) => {
  const amount = Number(cents || 0) / 100;
  return amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const yesNo = (value) => value === true ? "Sim" : value === false ? "Não" : "";

const slug = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "_")
  .replace(/^_+|_+$/g, "");

const ignoredObjectKeys = new Set([
  "id",
  "dataUrl",
  "photoUrl",
  "url",
  "fileUrl",
  "relativePath",
  "downloadPath",
  "downloadUrl",
]);

const fieldLabels = {
  name: "Nome",
  relationship: "Parentesco",
  cpf: "CPF",
  whatsapp: "WhatsApp",
  phone: "Telefone",
  phone1: "Telefone 1",
  phone2: "Telefone 2",
  educationLevel: "Escolaridade",
  incomeCents: "Renda",
  livesWithAttendee: "Reside com o atendido",
  institution: "Instituição",
  value: "Valor",
  status: "Status",
  notes: "Observações",
};

const valueForKey = (key, value) => {
  if (key === "incomeCents") return value === undefined || value === null || value === "" ? "" : currency(value);
  if (key === "livesWithAttendee") return yesNo(value);
  if (/date$/i.test(key)) return dateBr(value);
  if (key === "status") return statusLabels[value] || value;
  return printable(value);
};

function printable(value) {
  if (value === undefined || value === null || value === "") return "";
  if (typeof value === "boolean") return yesNo(value);
  if (Array.isArray(value)) {
    return value
      .map((item) => printable(item))
      .filter(Boolean)
      .join("\n");
  }
  if (typeof value === "object") {
    const entries = Object.entries(value)
      .filter(([key, child]) => !ignoredObjectKeys.has(key) && child !== undefined && child !== null && child !== "")
      .map(([key, child]) => {
        const rendered = valueForKey(key, child);
        return rendered ? `${fieldLabels[key] || key}: ${rendered}` : "";
      })
      .filter(Boolean);
    if (entries.length) return entries.join(" — ");
    if (value.name || value.label || value.title) return String(value.name || value.label || value.title);
    return "";
  }
  return String(value);
}

const personSummary = (person = {}) => printable({
  name: person.name,
  relationship: person.relationship,
  cpf: person.cpf,
  whatsapp: person.whatsapp,
  phone1: person.phone1,
  phone2: person.phone2,
  educationLevel: person.educationLevel,
  incomeCents: person.incomeCents,
  livesWithAttendee: person.livesWithAttendee,
});

const addressSummary = (address = {}) => {
  const first = [address.street, address.number].filter(Boolean).join(", ");
  return [first, address.complement, address.neighborhood, address.city, address.state, address.zipCode]
    .filter(Boolean)
    .join(" — ");
};

const longDate = () => new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "long",
  year: "numeric",
}).format(new Date());

const addVariable = (state, id, label, value, group = "Atendido") => {
  const normalizedId = slug(id);
  if (!normalizedId || state.ids.has(normalizedId)) return;
  state.ids.add(normalizedId);
  state.fields.push({ id: normalizedId, label, type: "text", group });
  state.values[normalizedId] = printable(value);
};

const flattenHiddenValues = (state, value, path = []) => {
  if (value === undefined || value === null || value === "") return;
  if (Array.isArray(value)) {
    const id = slug(path.join("_"));
    if (id) state.values[id] = printable(value);
    return;
  }
  if (typeof value !== "object") {
    const id = slug(path.join("_"));
    if (id) state.values[id] = printable(value);
    return;
  }
  Object.entries(value).forEach(([key, child]) => {
    if (["dataUrl", "photoUrl", "files"].includes(key)) return;
    flattenHiddenValues(state, child, [...path, key]);
  });
};

export const buildAttendeeDocumentVariables = ({
  attendee = {},
  classes = [],
  groups = [],
  courses = [],
  attendance = [],
  customAttendeeTabs = [],
} = {}) => {
  const state = { fields: [], values: {}, ids: new Set() };
  const klass = classes.find((item) => Number(item.id) === Number(attendee.classId));
  // "courses" e "courseId" permanecem apenas como compatibilidade com registros
  // e integrações anteriores à mudança de terminologia.
  const availableGroups = groups.length ? groups : courses;
  const group = availableGroups.find(
    (item) => Number(item.id) === Number(klass?.groupId ?? klass?.courseId),
  );
  const family = attendee.family || {};
  const responsible1 = family.responsible1 || {};
  const responsible2 = family.responsible2 || {};
  const members = Array.isArray(family.members) ? family.members : [];
  const residents = [responsible1, responsible2, ...members].filter((person) => person?.livesWithAttendee);
  const incomeCents = residents.reduce((sum, person) => sum + Number(person?.incomeCents || 0), 0);
  const attendanceEntries = attendance.flatMap((session) => (session.entries || [])
    .filter((entry) => Number(entry.attendeeId) === Number(attendee.id))
    .map((entry) => ({ ...entry, session })));
  const present = attendanceEntries.filter((entry) => entry.status === "PRESENT").length;
  const absent = attendanceEntries.filter((entry) => entry.status === "ABSENT").length;
  const justified = attendanceEntries.filter((entry) => entry.status === "JUSTIFIED").length;

  const common = [
    ["id_atendido", "ID do atendido", attendee.id],
    ["numero_matricula", "Número de matrícula", attendee.registrationNumber],
    ["ra", "RA", attendee.ra],
    ["nome_completo", "Nome completo", attendee.fullName],
    ["nome_social", "Nome social", attendee.socialName],
    ["data_inscricao", "Data de inscrição", dateBr(attendee.enrollmentDate)],
    ["data_nascimento", "Data de nascimento", dateBr(attendee.birthDate)],
    ["idade", "Idade", yearsFrom(attendee.birthDate)],
    ["genero", "Gênero", attendee.gender],
    ["etnia", "Etnia/cor", attendee.ethnicity],
    ["nacionalidade", "Nacionalidade", attendee.nationality],
    ["nis", "NIS", attendee.nis],
    ["cpf", "CPF", attendee.cpf],
    ["rg", "RG", attendee.rg],
    ["whatsapp", "WhatsApp", attendee.whatsapp],
    ["telefone", "Telefone", attendee.phone],
    ["email", "E-mail", attendee.email],
    ["status", "Status", statusLabels[attendee.status] || attendee.status],
    ["data_saida", "Data de saída", dateBr(attendee.exitDate)],
    ["motivo_saida", "Motivo de saída", attendee.exitReason],
    ["turma", "Turma", klass?.name],
    ["periodo_turma", "Período da turma", klass?.period],
    ["grupo", "Grupo", group?.name],
    ["coordenador_grupo", "Coordenador do grupo", group?.coordinator],
    ["data_vinculo_turma", "Data de vínculo com a turma", dateBr(attendee.classLinkedAt)],
    ["data_atual", "Data atual", new Date().toLocaleDateString("pt-BR")],
    ["data_atual_extenso", "Data atual por extenso", longDate()],
  ];
  common.forEach(([id, label, value]) => addVariable(state, id, label, value, "Dados do atendido"));
  state.values.matricula = state.values.numero_matricula;
  state.values.nome_atendido = state.values.nome_completo;
  state.values.data_matricula = state.values.data_inscricao;
  // Modelos salvos anteriormente continuam renderizando, sem voltar a expor
  // "Curso" na lista de variáveis do módulo de atendidos.
  state.values.curso = state.values.grupo;
  state.values.coordenador_curso = state.values.coordenador_grupo;

  const address = attendee.address || {};
  [
    ["cep", "CEP", address.zipCode], ["rua", "Rua", address.street],
    ["numero", "Número", address.number], ["complemento", "Complemento", address.complement],
    ["bairro", "Bairro", address.neighborhood], ["cidade", "Cidade", address.city],
    ["estado", "Estado", address.state], ["zona", "Zona", address.zone],
    ["tipo_moradia", "Tipo de moradia", address.housingType],
    ["endereco_completo", "Endereço completo", addressSummary(address)],
  ].forEach(([id, label, value]) => addVariable(state, id, label, value, "Endereço"));

  const addResponsible = (prefix, labelPrefix, person) => {
    [
      [`${prefix}_nome`, `${labelPrefix} — Nome`, person.name],
      [`${prefix}_cpf`, `${labelPrefix} — CPF`, person.cpf],
      [`${prefix}_whatsapp`, `${labelPrefix} — WhatsApp`, person.whatsapp],
      [`${prefix}_parentesco`, `${labelPrefix} — Parentesco`, person.relationship],
      [`${prefix}_escolaridade`, `${labelPrefix} — Escolaridade`, person.educationLevel],
      [`${prefix}_renda`, `${labelPrefix} — Renda`, person.incomeCents === undefined ? "" : currency(person.incomeCents)],
      [`${prefix}_reside_com_atendido`, `${labelPrefix} — Reside com o atendido`, yesNo(person.livesWithAttendee)],
    ].forEach(([id, label, value]) => addVariable(state, id, label, value, "Família e responsáveis"));
  };
  addResponsible("responsavel_1", "Responsável 1", responsible1);
  addResponsible("responsavel_2", "Responsável 2", responsible2);
  addVariable(state, "responsaveis", "Responsáveis", [responsible1, responsible2].map(personSummary).filter(Boolean), "Família e responsáveis");
  addVariable(state, "integrantes_familiares", "Integrantes familiares", members.map(personSummary), "Família e responsáveis");
  addVariable(state, "quantidade_residentes", "Quantidade de residentes", residents.length, "Família e responsáveis");
  addVariable(state, "renda_familiar_total", "Renda familiar total", currency(incomeCents), "Família e responsáveis");
  addVariable(state, "renda_per_capita", "Renda per capita", residents.length ? currency(Math.round(incomeCents / residents.length)) : "", "Família e responsáveis");

  const authorizations = attendee.authorizations?.pickupPersons || [];
  addVariable(
    state,
    "pessoas_autorizadas",
    "Pessoas autorizadas a buscar",
    authorizations.map(personSummary),
    "Autorizações",
  );

  const social = attendee.social || {};
  addVariable(state, "cras", "CRAS de referência", social.cras, "Social");
  addVariable(state, "renda_familiar_social", "Renda familiar informada no social", social.familyIncome, "Social");
  addVariable(state, "beneficios_sociais", "Benefícios sociais", social.benefits, "Social");

  const education = attendee.education || {};
  addVariable(state, "escolaridade", "Escolaridade", education.level, "Escolaridade");
  addVariable(state, "escola", "Escola", education.school, "Escolaridade");
  addVariable(state, "serie_ano", "Série/Ano", education.grade, "Escolaridade");
  addVariable(state, "periodo_escolar", "Período escolar", education.period, "Escolaridade");
  addVariable(state, "cursos_extras", "Cursos extras", education.extraCourses, "Escolaridade");

  addVariable(state, "total_aulas", "Total de aulas registradas", attendanceEntries.length, "Frequência");
  addVariable(state, "total_presencas", "Total de presenças", present, "Frequência");
  addVariable(state, "total_faltas", "Total de faltas", absent, "Frequência");
  addVariable(state, "total_justificadas", "Total de faltas justificadas", justified, "Frequência");
  addVariable(state, "percentual_presenca", "Percentual de presença", attendanceEntries.length ? `${Math.round((present / attendanceEntries.length) * 100)}%` : "", "Frequência");

  const customFields = attendee.customFields || {};
  const isConereTab = (title) => slug(title).includes("conere");
  const configuredTabs = Array.isArray(customAttendeeTabs) ? customAttendeeTabs : [];
  const configuredTitles = new Set(configuredTabs.map((tab) => String(tab?.title || "")));

  configuredTabs.forEach((tab) => {
    const title = String(tab?.title || "").trim();
    if (!title || isConereTab(title)) return;
    const values =
      customFields[title] && typeof customFields[title] === "object"
        ? customFields[title]
        : {};
    (Array.isArray(tab?.fields) ? tab.fields : []).forEach((field) => {
      const label = String(field?.label || "").trim();
      if (!label || isConereTab(label)) return;
      addVariable(
        state,
        `personalizado_${title}_${label}`,
        label,
        values[label],
        title,
      );
    });
  });

  Object.entries(customFields).forEach(([tab, values]) => {
    if (isConereTab(tab) || configuredTitles.has(tab)) return;
    Object.entries(values && typeof values === "object" ? values : {}).forEach(([field, value]) => {
      if (isConereTab(field)) return;
      addVariable(state, `personalizado_${tab}_${field}`, field, value, tab);
    });
  });

  // Mantém tokens técnicos antigos funcionando em modelos já salvos, mas eles
  // não entram em "fields" e portanto não aparecem mais na lista do editor.
  flattenHiddenValues(state, attendee, ["atendido"]);

  return { fields: state.fields, values: state.values };
};

export const groupAttendeeDocumentVariables = (fields = []) => fields.reduce((groups, field) => {
  const group = field.group || "Atendido";
  groups[group] = groups[group] || [];
  groups[group].push(field);
  return groups;
}, {});
